import { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const MAX_CHARS = 140;

const USER_ACCENTS = [
  "#C1440E", "#2D6A9F", "#2D9F6A", "#9F2D6A",
  "#6A2D9F", "#9F7A2D", "#2D4F9F", "#9F2D2D",
  "#2D9F9F", "#5C9F2D",
];

function getUserAccent(username) {
  let h = 0;
  for (let i = 0; i < (username || "").length; i++)
    h = (Math.imul(31, h) + username.charCodeAt(i)) | 0;
  return USER_ACCENTS[Math.abs(h) % USER_ACCENTS.length];
}

// Brick wall SVG background
const BrickPattern = () => (
  <svg style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, opacity: 1, pointerEvents: "none" }}
    xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="bricks" x="0" y="0" width="120" height="40" patternUnits="userSpaceOnUse">
        {/* Row 1 */}
        <rect x="0" y="0" width="118" height="18" fill="#c8735a" rx="0.5"/>
        <rect x="0" y="0" width="58" height="18" fill="#c06040" rx="0.5"/>
        <rect x="61" y="0" width="57" height="18" fill="#bf6244" rx="0.5"/>
        {/* Row 2 offset */}
        <rect x="-30" y="21" width="58" height="18" fill="#c86a50" rx="0.5"/>
        <rect x="31" y="21" width="58" height="18" fill="#c07055" rx="0.5"/>
        <rect x="92" y="21" width="58" height="18" fill="#b85e42" rx="0.5"/>
        {/* mortar */}
        <rect x="0" y="0" width="120" height="40" fill="none" stroke="#8B3A2A" strokeWidth="0.3"/>
        <line x1="60" y1="0" x2="60" y2="19" stroke="#8B3A2A" strokeWidth="0.5"/>
        <line x1="30" y1="21" x2="30" y2="40" stroke="#8B3A2A" strokeWidth="0.5"/>
        <line x1="90" y1="21" x2="90" y2="40" stroke="#8B3A2A" strokeWidth="0.5"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#bricks)"/>
  </svg>
);

function MessageBlock({ msg }) {
  const accent = getUserAccent(msg.username);
  const isLong = msg.text.length > 80;

  return (
    <div style={{
      backgroundColor: "rgba(252, 249, 243, 0.97)",
      borderLeft: `4px solid ${accent}`,
      padding: isLong ? "16px 18px" : "12px 16px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)",
      position: "relative",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      breakInside: "avoid",
      marginBottom: "10px",
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.2), 0 2px 6px ${accent}44`;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)";
    }}
    >
      {/* Top accent bar */}
      <div style={{
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "2px",
        background: `linear-gradient(90deg, ${accent}, transparent)`,
      }} />

      <p style={{
        color: "#1a1a1a",
        fontSize: isLong ? "13px" : "14px",
        lineHeight: "1.65",
        margin: "0 0 12px 0",
        fontFamily: "'Syne', sans-serif",
        fontWeight: 400,
        letterSpacing: "0.01em",
        wordBreak: "break-word",
      }}>
        {msg.text}
      </p>

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <span style={{
          fontSize: "11px",
          color: accent,
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          letterSpacing: "0.05em",
        }}>
          @{msg.username || "anónimo"}
        </span>
        <span style={{
          fontSize: "10px",
          color: "#999",
          fontFamily: "'Syne', sans-serif",
        }}>
          {new Date(msg.created_at).toLocaleDateString("es", { day: "2-digit", month: "short", year: "2-digit" })}
        </span>
      </div>
    </div>
  );
}

function AuthScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async () => {
    setError(null); setSuccess(null); setLoading(true);
    if (mode === "register") {
      const { error } = await supabase.auth.signUp({ email, password, options: { data: { username } } });
      if (error) setError(error.message);
      else setSuccess("¡Cuenta creada! Revisá tu email.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError("Email o contraseña incorrectos.");
    }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderBottom: "2px solid rgba(255,255,255,0.3)",
    color: "#f0ebe3",
    fontFamily: "'Syne', sans-serif",
    fontSize: "14px",
    padding: "12px 0 10px",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "20px",
    letterSpacing: "0.03em",
    background: "transparent",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
    }}>
      <BrickPattern />

      {/* Dark overlay */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 1,
        background: "linear-gradient(135deg, rgba(10,5,3,0.82) 0%, rgba(20,10,6,0.75) 100%)",
      }} />

      <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: "380px" }}>
        {/* Logo */}
        <div style={{ marginBottom: "48px" }}>
          <h1 style={{
            margin: "0 0 8px",
            fontSize: "42px",
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            color: "#f0ebe3",
            letterSpacing: "-0.02em",
            lineHeight: 1,
          }}>
            THE<br />WALL
          </h1>
          <p style={{
            margin: 0,
            fontSize: "11px",
            color: "#C1440E",
            fontFamily: "'Syne', sans-serif",
            fontWeight: 600,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
          }}>
            Tus palabras · Para siempre
          </p>
        </div>

        {/* Toggle */}
        <div style={{ display: "flex", gap: "24px", marginBottom: "32px" }}>
          {["login", "register"].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(null); setSuccess(null); }} style={{
              background: "none", border: "none",
              color: mode === m ? "#f0ebe3" : "rgba(240,235,227,0.3)",
              fontFamily: "'Syne', sans-serif",
              fontSize: "13px", fontWeight: 700,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer", padding: "0 0 4px",
              borderBottom: mode === m ? "2px solid #C1440E" : "2px solid transparent",
              transition: "all 0.2s",
            }}>
              {m === "login" ? "Entrar" : "Registrarse"}
            </button>
          ))}
        </div>

        {mode === "register" && (
          <input type="text" placeholder="Usuario" value={username}
            onChange={e => setUsername(e.target.value)} style={inputStyle} />
        )}
        <input type="email" placeholder="Email" value={email}
          onChange={e => setEmail(e.target.value)} style={inputStyle} />
        <input type="password" placeholder="Contraseña" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()} style={inputStyle} />

        {error && <p style={{ color: "#E85D4A", fontSize: "12px", marginBottom: "12px", fontFamily: "'Syne', sans-serif" }}>{error}</p>}
        {success && <p style={{ color: "#3ABFA8", fontSize: "12px", marginBottom: "12px", fontFamily: "'Syne', sans-serif" }}>{success}</p>}

        <button onClick={handleSubmit} disabled={loading} style={{
          width: "100%",
          backgroundColor: "#C1440E",
          color: "#fff",
          border: "none",
          padding: "14px",
          fontFamily: "'Syne', sans-serif",
          fontSize: "13px", fontWeight: 700,
          letterSpacing: "0.15em", textTransform: "uppercase",
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.7 : 1,
          transition: "background 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = "#a33509"}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = "#C1440E"}
        >
          {loading ? "..." : mode === "login" ? "Entrar →" : "Crear cuenta →"}
        </button>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');
        input::placeholder { color: rgba(240,235,227,0.35); }
      `}</style>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wallRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const { data, error } = await supabase.from("messages").select("*").order("created_at", { ascending: true });
      if (error) setError("Error cargando mensajes.");
      else setMessages(data || []);
      setLoading(false);
    };
    fetch();

    const channel = supabase.channel("messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => {
        setMessages(prev => [...prev, payload.new]);
        setTimeout(() => wallRef.current?.scrollTo({ top: wallRef.current.scrollHeight, behavior: "smooth" }), 100);
      }).subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > MAX_CHARS || !session) return;

    const username = session.user.user_metadata?.username || session.user.email.split("@")[0];

    const { error } = await supabase.from("messages").insert([{
      text: trimmed, username, country: null, user_id: session.user.id,
    }]);

    if (error) {
      if (error.code === "23505" || error.message?.includes("unique") || error.message?.includes("duplicate") || error.message?.includes("one_message_per_day")) {
        setError("Ya grabaste tu mensaje hoy. Volvé mañana. 🧱");
      } else {
        setError("Error al publicar. Intentá de nuevo.");
      }
      setTimeout(() => setError(null), 4000);
      return;
    }

    setDraft("");
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  }, [draft, session]);

  const handleLogout = async () => await supabase.auth.signOut();

  if (!session) return <AuthScreen />;

  const username = session.user.user_metadata?.username || session.user.email.split("@")[0];
  const remaining = MAX_CHARS - draft.length;
  const isOverLimit = remaining < 0;
  const isNearLimit = remaining <= 20 && remaining >= 0;

  return (
    <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      <BrickPattern />

      {/* Overlay gradient — lighter in center for readability */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 1,
        background: "linear-gradient(180deg, rgba(10,5,3,0.75) 0%, rgba(10,5,3,0.55) 10%, rgba(10,5,3,0.55) 85%, rgba(10,5,3,0.85) 100%)",
        pointerEvents: "none",
      }} />

      {/* HEADER */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0,
        zIndex: 100,
        padding: "16px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(10,5,3,0.6)",
      }}>
        {/* Logo */}
        <div>
          <span style={{
            fontSize: "22px",
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            color: "#f0ebe3",
            letterSpacing: "-0.02em",
          }}>
            THE WALL
          </span>
          <span style={{
            marginLeft: "12px",
            fontSize: "10px",
            color: "#C1440E",
            fontFamily: "'Syne', sans-serif",
            fontWeight: 600,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            opacity: 0.9,
          }}>
            para siempre
          </span>
        </div>

        {/* User + logout */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{
            fontSize: "12px",
            color: "rgba(240,235,227,0.5)",
            fontFamily: "'Syne', sans-serif",
            fontWeight: 600,
          }}>
            @{username}
          </span>
          <button onClick={handleLogout} style={{
            background: "none",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "rgba(240,235,227,0.4)",
            fontFamily: "'Syne', sans-serif",
            fontSize: "11px", fontWeight: 600,
            letterSpacing: "0.1em",
            padding: "5px 12px",
            cursor: "pointer",
            textTransform: "uppercase",
            transition: "all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.color = "#f0ebe3"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(240,235,227,0.4)"; }}
          >
            Salir
          </button>
        </div>
      </div>

      {/* WALL — masonry grid */}
      <div
        ref={wallRef}
        style={{
          position: "relative", zIndex: 2,
          paddingTop: "80px",
          paddingBottom: "140px",
          paddingLeft: "24px",
          paddingRight: "24px",
          maxWidth: "1200px",
          margin: "0 auto",
          columnCount: 3,
          columnGap: "12px",
          minHeight: "100vh",
        }}
      >
        <style>{`
          @media (max-width: 900px) { .wall-grid { column-count: 2 !important; } }
          @media (max-width: 600px) { .wall-grid { column-count: 1 !important; } }
          @keyframes blockIn {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {loading && (
          <div style={{
            columnSpan: "all", textAlign: "center", padding: "60px 0",
            color: "rgba(240,235,227,0.3)", fontFamily: "'Syne', sans-serif", fontSize: "13px",
          }}>
            Cargando el muro...
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={msg.id || i} style={{ animation: "blockIn 0.4s ease both", animationDelay: `${Math.min(i * 0.02, 0.5)}s` }}>
            <MessageBlock msg={msg} />
          </div>
        ))}
      </div>

      {/* WRITE ZONE */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        zIndex: 100,
        borderTop: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(12px)",
        backgroundColor: "rgba(10,5,3,0.85)",
        padding: "16px 32px",
      }}>
        {error && (
          <div style={{
            color: "#E85D4A", fontSize: "11px", marginBottom: "8px",
            fontFamily: "'Syne', sans-serif", fontWeight: 600,
          }}>{error}</div>
        )}

        {!submitted ? (
          <div style={{ display: "flex", gap: "12px", alignItems: "center", maxWidth: "800px", margin: "0 auto" }}>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder="Grabá tu huella en el muro..."
              rows={2}
              style={{
                flex: 1,
                backgroundColor: "rgba(255,255,255,0.06)",
                border: "1px solid",
                borderColor: isOverLimit ? "#E85D4A" : isNearLimit ? "#E8A838" : "rgba(255,255,255,0.12)",
                color: "#f0ebe3",
                fontFamily: "'Syne', sans-serif",
                fontSize: "14px",
                lineHeight: "1.6",
                padding: "10px 16px",
                resize: "none",
                outline: "none",
                letterSpacing: "0.01em",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
              <span style={{
                fontSize: "11px",
                fontFamily: "'Syne', sans-serif",
                color: isOverLimit ? "#E85D4A" : isNearLimit ? "#E8A838" : "rgba(255,255,255,0.2)",
                fontWeight: 600,
              }}>{remaining}</span>
              <button
                onClick={handleSubmit}
                disabled={!draft.trim() || isOverLimit}
                style={{
                  backgroundColor: draft.trim() && !isOverLimit ? "#C1440E" : "rgba(255,255,255,0.05)",
                  color: draft.trim() && !isOverLimit ? "#fff" : "rgba(255,255,255,0.2)",
                  border: "none",
                  padding: "10px 20px",
                  fontFamily: "'Syne', sans-serif",
                  fontSize: "12px", fontWeight: 700,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  cursor: draft.trim() && !isOverLimit ? "pointer" : "not-allowed",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { if (draft.trim() && !isOverLimit) e.currentTarget.style.backgroundColor = "#a33509"; }}
                onMouseLeave={e => { if (draft.trim() && !isOverLimit) e.currentTarget.style.backgroundColor = "#C1440E"; }}
              >
                Grabar →
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: "center", padding: "10px",
            color: "#C1440E", fontFamily: "'Syne', sans-serif",
            fontSize: "13px", fontWeight: 700, letterSpacing: "0.1em",
          }}>
            Tu voz quedó grabada en el muro para siempre. ✓
          </div>
        )}

        <div style={{
          textAlign: "center", marginTop: "6px",
          fontSize: "10px", opacity: 0.2,
          color: "#f0ebe3", fontFamily: "'Syne', sans-serif",
          letterSpacing: "0.1em",
        }}>
          Enter para publicar · permanente · sin edición
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { overflow-x: hidden; }
        textarea::placeholder { color: rgba(240,235,227,0.25); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); }
        @keyframes blockIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
